import { Autocomplete, Grid, Slider, Switch, TextField, Divider, MenuItem, ToggleButtonGroup, ToggleButton, Icon, Collapse, Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";
import deviceAPI from "services/api/bks";
import { useSoftUIController } from "hooks/context";
import { useMemo, useState } from "react";
import SoftSelect from "components/SoftComponents/SoftSelect";

// Reusable styles for SweetAlerts
const swalTitleStyle = "font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px";
const swalTextStyle = "font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px";

function BKSDeviceSettings({ selected_device, tags }) {
    const auth0 = useAuth0();
    deviceAPI.loadToken(auth0);

    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    const canDeviceEdit = userPermissions.includes('devices:edit');
    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');
    const canAdvancedDeviceEdit = userPermissions.includes('devices-advanced:edit');

    const [showAdvSettings, setShowAdvSettings] = useState(false);

    const queryClient = useQueryClient();

    const deviceConfigs = useQuery({
        queryKey: ['getBKDeviceConfigs', selected_device.config.device_type, selectedOrg],
        enabled: canAdvancedDeviceEdit,
        queryFn: async () => {
            return await deviceAPI.getBKDeviceConfigs(selected_device.config.device_type);
        }
    });

    const defaultValues = {
        uuid: selected_device.uuid,
        device_id: selected_device.device_id,
        organisation_id: selected_device.organisation_id,
        db_type: selected_device.db_type,
        name: selected_device.name,
        serial_number: selected_device.serial_number,
        active: selected_device.active,
        position: { longitude: selected_device.position[0], latitude: selected_device.position[1] },
        site_id: selected_device.stop?.site_id ?? null,
        stop_label: selected_device.stop?.stop_label ?? null,
        tts_volume: selected_device.config?.tts?.enabled ? selected_device.config.tts.volume : null,

        // New Editable Fields
        hardware_type: selected_device.hardware_type,
        liveview_type: selected_device?.config?.liveview_type,
        displays: selected_device?.config?.displays || [],
        behaviour: selected_device?.config?.behaviour,
        skin: selected_device?.config?.skin,
        firmware: selected_device?.config?.firmware
    };

    const memoizedConfigs = useMemo(() => {
        if (!deviceConfigs.data) {
            return {
                behaviours: [{ value: defaultValues.behaviour, label: defaultValues.behaviour }],
                skins: [{ value: defaultValues.skin, label: defaultValues.skin }],
                firmwares: []
            };
        }

        const firmwares = [];
        for (var [key, values] of Object.entries(deviceConfigs.data.firmwares)) {
            values.forEach(v => {
                firmwares.push({ category: key, label: v, value: v });
            });
        }

        const behaviours = deviceConfigs.data.behaviours.map(b => { return { value: b, label: b } })
        const skins = deviceConfigs.data.skins.map(s => { return { value: s, label: s } })

        return { behaviours, skins, firmwares };
    }, [deviceConfigs.data]);

    const {
        control,
        register,
        handleSubmit,
        setValue
    } = useForm({ defaultValues });

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (values) => deviceAPI.updateBKSDeviceData({ ...values, canAdvancedDeviceEdit }),
        onMutate: () => {
            Swal.fire({
                title: `<div style="${swalTitleStyle}">Saving...</div>`,
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => Swal.showLoading()
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['getDevice', selected_device.uuid, selectedOrg] });
            queryClient.invalidateQueries({ queryKey: ['getAllDevices', selectedOrg] });
            Swal.fire({
                icon: 'success',
                title: `<div style="${swalTitleStyle}">Saved!</div>`,
                showConfirmButton: false,
                timer: 2000
            });
        },
        onError: (error) => {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: `<div style="${swalTitleStyle}">Error</div>`,
                html: `<div style="${swalTextStyle}">${error.message || "There was a problem saving your data. Ensure the input is unique."}</div>`,
            });
        }
    });

    const triggerDeviceReboot = async () => {
        try {
            const response = await deviceAPI.reboot(selected_device.uuid);
            if (!response) throw Error("Reboot failed");

            await Swal.fire({
                icon: "success",
                title: `<div style="${swalTitleStyle}">Device Rebooting!</div>`,
                showConfirmButton: false,
                timer: 2000
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: `<div style="${swalTitleStyle}">Error</div>`,
                text: "There was a problem rebooting the device.",
            });
        }
    };

    return (
        <SoftBox
            component="form"
            onSubmit={handleSubmit(mutateAsync)}
            style={{ height: deviceLayout === 'mobile' ? 'calc(100svh - 8.5rem - 16px)' : '100%' }}
        >
            <SoftBox display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                <SoftBox>
                    <SoftBox px={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ background: '#f9f9f9' }} >
                        <SoftTypography variant={deviceLayout === 'mobile' ? "h5" : "h4"}>
                            Device Settings
                        </SoftTypography>
                        <SoftBox pl={1} my={1.5}>
                            <Controller
                                name="active"
                                control={control}
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        color="primary"
                                        value={field.value ?? defaultValues.active}
                                        exclusive
                                        onChange={(event, newValue) => {
                                            if (newValue !== null) {
                                                field.onChange(newValue);
                                            }
                                        }}
                                        aria-label="Active Status Toggle"
                                    >
                                        <ToggleButton
                                            value={true}
                                            sx={{
                                                px: 3,
                                                fontSize: '0.75rem !important',
                                                lineHeight: 1,
                                                borderTopLeftRadius: '8px',
                                                borderBottomLeftRadius: '8px',
                                                fontWeight: 'bold',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#6cb16e',
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: '#5a9e5d' }
                                                }
                                            }}
                                        >
                                            Active
                                        </ToggleButton>
                                        <ToggleButton
                                            value={false}
                                            sx={{
                                                px: 3,
                                                fontSize: '0.75rem !important',
                                                lineHeight: 1,
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                                fontWeight: 'bold',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#da9c93',
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: '#e46562' }
                                                }
                                            }}
                                        >
                                            Inactive
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            />
                        </SoftBox>
                    </SoftBox>
                    <Divider />

                    {/* Basic Info */}
                    <Grid container spacing={2} px={2} pt={2} pb={1}>
                        <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Device Name</SoftTypography>
                            </SoftBox>
                            <SoftInput placeholder="Device Name" {...register('name')} />
                        </Grid>
                        <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Serial Number</SoftTypography>
                            </SoftBox>
                            <SoftInput
                                placeholder="Serial Number"
                                disabled={!(showAdvancedDeviceLevel && canAdvancedDeviceEdit)}
                                {...register('serial_number')}
                            />
                        </Grid>
                    </Grid>

                    {/* Stop configuration */}
                    {selected_device.stop && (
                        <Grid container spacing={2} px={2} py={1}>
                            <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                <SoftBox pl={1} my={0.5}>
                                    <SoftTypography variant="body2" fontWeight='bold'>Stop ID</SoftTypography>
                                </SoftBox>
                                {tags?.stops?.length > 0 ? (
                                    <Controller
                                        name="site_id"
                                        control={control}
                                        render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                                            const isOverride = value && !tags.stops.some((s) => s.site_id === value);
                                            const shouldLock = isOverride && !canAdvancedDeviceEdit;

                                            return (
                                                <Autocomplete
                                                    id="stop-id-select"
                                                    disabled={shouldLock}
                                                    freeSolo={showAdvancedDeviceLevel && canAdvancedDeviceEdit}
                                                    selectOnFocus
                                                    clearOnBlur
                                                    handleHomeEndKeys
                                                    options={tags.stops.map(row => ({
                                                        ...row,
                                                        stop_id: row.stop_id ?? row.site_id
                                                    }))}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === "string") return option.replace(/\s/g, '');
                                                        if (option.inputValue) return option.inputValue;
                                                        return option.stop_id ? `${option.stop_id} - ${option.site_name}` : "";
                                                    }}
                                                    value={
                                                        tags.stops.find((opt) => opt.site_id === value) ||
                                                        (value ? { stop_id: value, site_name: "Manual Override" } : null)
                                                    }
                                                    filterOptions={(options, params) => {
                                                        const cleanInput = params.inputValue.replace(/\s/g, '');
                                                        const filtered = options.filter((o) =>
                                                            `${o.stop_id} ${o.site_name}`.toLowerCase().includes(params.inputValue.toLowerCase())
                                                        );

                                                        if (showAdvancedDeviceLevel && canAdvancedDeviceEdit && cleanInput && !options.some((o) => cleanInput === o.site_id)) {
                                                            filtered.push({
                                                                inputValue: cleanInput,
                                                                site_name: `Override: Use "${cleanInput}"`,
                                                                stop_id: cleanInput,
                                                            });
                                                        }
                                                        return filtered;
                                                    }}
                                                    onChange={(event, newValue) => {
                                                        let finalValue = null;
                                                        let isManual = false;
                                                        let finalPosition = null;

                                                        if (typeof newValue === "string") {
                                                            finalValue = newValue.replace(/\s/g, '');
                                                            isManual = true;
                                                        } else if (newValue?.inputValue) {
                                                            finalValue = newValue.inputValue;
                                                            isManual = true;
                                                        } else if (newValue) {
                                                            finalValue = newValue.site_id;
                                                            finalPosition = newValue.position;
                                                        }

                                                        if (showAdvancedDeviceLevel && canAdvancedDeviceEdit && isManual && finalValue) {
                                                            Swal.fire({
                                                                title: `<div style="${swalTitleStyle}">Manual Override</div>`,
                                                                html: `<div style="${swalTextStyle}">You are entering a custom Site ID: ${finalValue}.</div>`,
                                                                icon: 'warning',
                                                                confirmButtonColor: '#ed6c02'
                                                            });
                                                        }

                                                        if (canDeviceEdit || canAdvancedDeviceEdit) {
                                                            onChange(finalValue);
                                                            if (finalPosition) {
                                                                setValue('position', finalPosition, { shouldValidate: true, shouldDirty: true });
                                                            }
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            inputRef={ref}
                                                            variant="outlined"
                                                            placeholder={showAdvancedDeviceLevel && canAdvancedDeviceEdit ? "Select or type override..." : "Select a Stop..."}
                                                            error={!!error}
                                                            helperText={
                                                                shouldLock ? "Locked: Manual override detected. Admins only can edit."
                                                                    : isOverride ? "Manual Override Active (Spaces Removed)"
                                                                        : error?.message || ""
                                                            }
                                                            sx={{
                                                                "& .MuiFormHelperText-root": {
                                                                    color: isOverride ? "#ed6c02" : "inherit",
                                                                    fontWeight: 600,
                                                                },
                                                                "& .MuiInputBase-root.Mui-disabled": {
                                                                    backgroundColor: "rgba(0, 0, 0, 0.05)"
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            );
                                        }}
                                    />
                                ) : (
                                    <SoftInput placeholder="Stop ID" {...register('site_id')} />
                                )}
                            </Grid>
                            <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                <SoftBox pl={1} my={0.5}>
                                    <SoftTypography variant="body2" fontWeight='bold'>Stop Label</SoftTypography>
                                </SoftBox>
                                <SoftInput placeholder="Stop Label" {...register('stop_label')} />
                            </Grid>
                        </Grid>
                    )}

                    {selected_device?.config?.tts?.enabled === 1 && (
                        <Grid container spacing={2} px={2} py={1}>
                            <Grid item xxs={12}>
                                <SoftBox my={1.5}>
                                    <SoftBox pl={1}>
                                        <SoftTypography variant='body2' fontWeight='bold'>TTS Volume</SoftTypography>
                                    </SoftBox>
                                    <Controller
                                        name="tts_volume"
                                        control={control}
                                        render={({ field }) => (
                                            <Slider
                                                step={1} marks min={1} max={9}
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(value) => `${value * 10}%`}
                                                sx={{ py: 1.25, "& .MuiSlider-rail": { opacity: 1 }, '& .MuiSlider-valueLabel': { color: '#fff' } }}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                value={field.value ?? defaultValues.tts_volume}
                                            />
                                        )}
                                    />
                                </SoftBox>
                            </Grid>
                        </Grid>
                    )}

                    {(canAdvancedDeviceEdit && selected_device.hardware_type.includes('bk')) && (
                        <>
                            <SoftBox m={2}>
                                <Divider />
                            </SoftBox>

                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                onClick={() => setShowAdvSettings(!showAdvSettings)}
                                p={1.5}
                                mx={1.5}
                                sx={{
                                    borderRadius: showAdvSettings ? '8px 8px 0px 0px' : '8px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s',
                                    '&:hover': { backgroundColor: '#e9ecef' }
                                }}
                            >
                                <SoftTypography variant="button" fontWeight="bold" color="text">
                                    {'Advanced Device Settings'}
                                </SoftTypography>
                                <Icon color="secondary">{showAdvSettings ? 'expand_less' : 'expand_more'}</Icon>
                            </SoftBox>
                            <Collapse in={showAdvSettings}>

                                {/* Hardware Configurations (Behaviour, Liveview Type, Skin, Firmware) */}
                                <Grid container spacing={2} px={2} py={1}>
                                    <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                        <SoftBox pl={1} my={0.5}>
                                            <SoftTypography variant="body2" fontWeight='bold'>Behaviour</SoftTypography>
                                        </SoftBox>
                                        <Controller
                                            name={'behaviour'}
                                            control={control}
                                            render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                                                const isOverride = value && !memoizedConfigs.behaviours.some(opt => opt.value === value);
                                                const shouldLock = isOverride && !canAdvancedDeviceEdit;

                                                return (
                                                    <Autocomplete
                                                        id="behaviour-autocomplete"
                                                        disabled={shouldLock}
                                                        freeSolo={showAdvancedDeviceLevel && canAdvancedDeviceEdit}
                                                        selectOnFocus
                                                        clearOnBlur
                                                        handleHomeEndKeys
                                                        options={memoizedConfigs.behaviours.map(opt => opt.value)}
                                                        value={value || null}
                                                        onChange={(event, newValue) => onChange(newValue)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                inputRef={ref}
                                                                variant="outlined"
                                                                placeholder={showAdvancedDeviceLevel && canAdvancedDeviceEdit ? "Select or type override..." : "Select Behaviour..."}
                                                                error={!!error}
                                                                helperText={
                                                                    shouldLock ? "Locked: Manual override detected. Admins only can edit."
                                                                        : isOverride ? "Manual Override Active"
                                                                            : error?.message || ""
                                                                }
                                                                sx={{
                                                                    "& .MuiFormHelperText-root": {
                                                                        color: isOverride ? "#ed6c02" : "inherit",
                                                                        fontWeight: 600,
                                                                    },
                                                                    "& .MuiInputBase-root.Mui-disabled": {
                                                                        backgroundColor: "rgba(0, 0, 0, 0.05)"
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                        <SoftBox pl={1} my={0.5}>
                                            <SoftTypography variant="body2" fontWeight='bold'>Liveview Type</SoftTypography>
                                        </SoftBox>
                                        <Controller
                                            name="liveview_type"
                                            control={control}
                                            render={({ field }) => (
                                                <ToggleButtonGroup
                                                    color="primary"
                                                    value={field.value ?? defaultValues.liveview_type}
                                                    exclusive
                                                    onChange={(event, newValue) => {
                                                        if (newValue !== null) {
                                                            field.onChange(newValue);
                                                        }
                                                    }}
                                                    aria-label="Liveview Type Toggle"
                                                >
                                                    <ToggleButton
                                                        value={'on'}
                                                        sx={{
                                                            px: 3,
                                                            fontSize: '0.75rem !important',
                                                            lineHeight: 1,
                                                            borderTopLeftRadius: '8px',
                                                            borderBottomLeftRadius: '8px',
                                                            fontWeight: 'bold',
                                                            '&.Mui-selected': {
                                                                backgroundColor: '#6cb16e',
                                                                color: '#fff',
                                                                '&:hover': { backgroundColor: '#5a9e5d' }
                                                            }
                                                        }}
                                                    >
                                                        Live
                                                    </ToggleButton>
                                                    <ToggleButton
                                                        value={'history'}
                                                        sx={{
                                                            px: 3,
                                                            fontSize: '0.75rem !important',
                                                            lineHeight: 1,
                                                            borderTopRightRadius: '8px',
                                                            borderBottomRightRadius: '8px',
                                                            fontWeight: 'bold',
                                                            '&.Mui-selected': {
                                                                backgroundColor: '#526d9e',
                                                                color: '#fff',
                                                                '&:hover': { backgroundColor: '#3d638f' }
                                                            }
                                                        }}
                                                    >
                                                        History
                                                    </ToggleButton>
                                                </ToggleButtonGroup>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                        <SoftBox pl={1} my={0.5}>
                                            <SoftTypography variant="body2" fontWeight='bold'>Skin</SoftTypography>
                                        </SoftBox>
                                        <Controller
                                            name={'skin'}
                                            control={control}
                                            render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                                                const isOverride = value && !memoizedConfigs.skins.some(opt => opt.value === value);
                                                const shouldLock = isOverride && !canAdvancedDeviceEdit;

                                                return (
                                                    <Autocomplete
                                                        id="skin-autocomplete"
                                                        disabled={shouldLock}
                                                        freeSolo={showAdvancedDeviceLevel && canAdvancedDeviceEdit}
                                                        selectOnFocus
                                                        clearOnBlur
                                                        handleHomeEndKeys
                                                        options={memoizedConfigs.skins.map(opt => opt.value)}
                                                        value={value || null}
                                                        onChange={(event, newValue) => onChange(newValue)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                inputRef={ref}
                                                                variant="outlined"
                                                                placeholder={showAdvancedDeviceLevel && canAdvancedDeviceEdit ? "Select or type override..." : "Select Skin..."}
                                                                error={!!error}
                                                                helperText={
                                                                    shouldLock ? "Locked: Manual override detected. Admins only can edit."
                                                                        : isOverride ? "Manual Override Active"
                                                                            : error?.message || ""
                                                                }
                                                                sx={{
                                                                    "& .MuiFormHelperText-root": {
                                                                        color: isOverride ? "#ed6c02" : "inherit",
                                                                        fontWeight: 600,
                                                                    },
                                                                    "& .MuiInputBase-root.Mui-disabled": {
                                                                        backgroundColor: "rgba(0, 0, 0, 0.05)"
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                        <SoftBox pl={1} my={0.5}>
                                            <SoftTypography variant="body2" fontWeight='bold'>Firmware</SoftTypography>
                                        </SoftBox>
                                        <Controller
                                            name={'firmware'}
                                            control={control}
                                            render={({ field }) => {
                                                return (
                                                    <SoftSelect
                                                        isDisabled={true}
                                                        value={memoizedConfigs.firmwares.find(opt => opt.label === field?.value) || { value: defaultValues.firmware, label: defaultValues.firmware }}
                                                        onChange={(selected) => {
                                                            field.onChange(selected)
                                                        }}
                                                        groupBy={(option) => option.category}
                                                        options={memoizedConfigs.firmwares}
                                                        isSearchable={true}
                                                    />
                                                )
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <SoftBox m={2}>
                                    <Divider />
                                </SoftBox>

                                {/* Display Offsets - positive values will move it left/up, negative values will move it right/down*/}
                                <Grid container spacing={2} px={2} pt={1} pb={2}>
                                    {defaultValues.displays.map((display, dIndex) => (
                                        <>
                                            <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                                <SoftBox display="flex" gap={1} pl={1}>
                                                    <SoftTypography variant='body2' fontWeight='bold'>Display {dIndex + 1} Offset X</SoftTypography>
                                                    {!dIndex && (
                                                        <Tooltip title={<div>Positive values will move the display <b>up / left</b>.<br />Negative values will move the display <b>down / right</b>.</div>} placement="right" sx={{ paddingTop: '2px', paddingLeft: '4px' }}>
                                                            <Icon fontSize="small">help_outline</Icon>
                                                        </Tooltip>
                                                    )}
                                                </SoftBox>
                                                <Controller
                                                    name={`displays[${dIndex}].offset_x`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Slider
                                                            step={4} marks min={-20} max={20}
                                                            valueLabelDisplay="auto"
                                                            onChange={(e) => field.onChange(e.target.value)}
                                                            value={field.value ?? defaultValues.displays[dIndex].offset_x}
                                                            sx={{
                                                                py: 1.25,
                                                                '& .MuiSlider-thumb': { zIndex: 1 },
                                                                "& .MuiSlider-rail": { opacity: 1 },
                                                                '& .MuiSlider-valueLabel': { color: '#fff' }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                                                <SoftBox pl={1}>
                                                    <SoftTypography variant='body2' fontWeight='bold'>Display {dIndex + 1} Offset Y</SoftTypography>
                                                </SoftBox>
                                                <Controller
                                                    name={`displays[${dIndex}].offset_y`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Slider
                                                            step={4} marks min={-20} max={20}
                                                            valueLabelDisplay="auto"
                                                            onChange={(e) => field.onChange(e.target.value)}
                                                            value={field.value ?? defaultValues.displays[dIndex].offset_y}
                                                            sx={{
                                                                py: 1.25,
                                                                '& .MuiSlider-thumb': { zIndex: 1 },
                                                                "& .MuiSlider-rail": { opacity: 1 },
                                                                '& .MuiSlider-valueLabel': { color: '#fff' }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </>
                                    ))}
                                </Grid>
                            </Collapse>
                        </>
                    )}
                </SoftBox>


                <SoftBox>
                    <Divider />
                    <SoftBox sx={{ background: '#f9f9f9' }} display='flex' flexDirection='row' justifyContent='space-between' p={2}>
                        <SoftButton type="submit" variant="gradient" color="dark" disabled={isPending}>
                            Save
                        </SoftButton>
                        {selected_device?.hardware_type?.includes('bk') && (
                            <SoftButton variant="gradient" color="dark" onClick={triggerDeviceReboot}>
                                Reboot Device
                            </SoftButton>
                        )}
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    );
}

BKSDeviceSettings.propTypes = {
    selected_device: PropTypes.object.isRequired,
    tags: PropTypes.object
};

export default BKSDeviceSettings;